import type { Distance, Point, Radian, RGBColor } from "@pdfking/math"
import { Abs, Cos, Max, Min, PI, Round, Sin, Sqrt, Tan, Total } from "@pdfking/math"
import { ZERO_RECTANGLE } from "@pdfking/math/constants"
import type { PageNumber, RequestRaster } from "@pdfking/renderer-api"
import { ClipPolygon } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import { RecordAssign, Repeat, RecordMerge, WaitPromises, Void, Constant } from "@pdfking/tfunc"
import type {
  Bone as BoneType,
  BoxGeometry as BoxGeometryType,
  BufferGeometry as BufferGeometryType,
  Euler as EulerType,
  LineBasicMaterial,
  Material as MaterialType,
  Mesh as MeshType,
  SpotLight as SpotLightType,
  Texture,
  Vector3 as Vector3Type,
} from "three"

import type { Cover, FoldableMesh, FoldCell, PageMesh } from "#public"
import type { SettingsValues } from "#public"
import { CreateFoldRig, FoldClosedTarget, FoldOpenTarget } from "#public"
import type { BookTexture, PageTextureLayer, Renderer, Viewer } from "#public"

import { GetThree } from "../../internal/threejs/GetThree"
import { CurvatureAngles, CurvatureMinSegments } from "../../internal/utils/Curvature"
import { InsidePolygon } from "../../internal/utils/InsidePolygon"
import { defaults } from "../types/Settings"

const PRELOADED_PAGES = 3

type SheetTextures = {
  back: BookTexture[]
  front: BookTexture[]
}

// A raster is one page's content, whichever book pages show it
const RasterKey = ({ guid }: PageTextureLayer): string => guid

const HasValue = <T>(value: T | undefined): value is T => !NullQ(value)

// Axis helpers
const get_is_y = (settings: SettingsValues): boolean => settings.book.opening_axis === "y"
const get_rotation_axis = (settings: SettingsValues): keyof Point<unknown> =>
  get_is_y(settings) ? "x" : "y"
const get_pos_axis = (settings: SettingsValues): keyof Point<unknown> =>
  get_is_y(settings) ? "y" : "x"

// Animation mode helper
const is_stripe_animation = (settings: SettingsValues): boolean => settings.animation === "stripe"

// Get maximum book dimensions across all blocks
const get_book_dimensions = (settings: SettingsValues) =>
  settings.book.blocks.reduce(
    (acc, block) => ({
      height: Max(acc.height, block.size.height),
      width: Max(acc.width, block.size.width),
    }),
    ZERO_RECTANGLE
  )

const get_total_sheets = (settings: SettingsValues): number =>
  Total(...settings.book.blocks.map(block => block.sheets))

// A book of one page that folds and no cover is that page folding: a frame
// per step it opens at, then one per step it folds back, to its back
const get_folding = (settings: SettingsValues) => {
  const [block, ...rest] = settings.book.blocks
  return !settings.book.cover && block && !rest.length && block.folds.length ? block : undefined
}

// The last step a sheet opens at
const last_step = (folds: FoldCell[]): number => Max(0, ...folds.map(fold => fold.step))

export const CreateBookViewer = async (
  container: HTMLElement,
  initialSettings: Partial<SettingsValues>,
  // Asked for a texture's raster once its sheet is in view (see set_textures);
  // without one no page has a raster
  request_raster: RequestRaster = () => Promise.resolve(undefined),
  renderer_kind: Renderer | undefined = undefined
): Promise<Viewer> => {
  const {
    enum: kind,
    renderer,
    three,
    three: {
      AmbientLight,
      ArrowHelper,
      AxesHelper,
      Bone,
      BoxGeometry,
      BufferGeometry,
      Timer,
      Color,
      DataTexture,
      Euler,
      Float32BufferAttribute,
      Mesh,
      MeshStandardMaterial,
      OrbitControls,
      PerspectiveCamera,
      Scene,
      Skeleton,
      ShapeUtils,
      SkinnedMesh,
      SpotLight,
      SRGBColorSpace,
      Uint16BufferAttribute,
      Vector2,
      Vector3,
    },
  } = await GetThree(renderer_kind !== "webgpu")

  // Bone chain creation helper
  // extra_bone: true for page meshes (need one more bone than segments), false for foldables (one bone per segment)
  const create_bone_chain = (sizes: number[], is_y: boolean, extra_bone = true): BoneType[] => {
    const bone_count = extra_bone ? sizes.length + 1 : sizes.length
    const bones: BoneType[] = []
    for (let i = 0; i < bone_count; i++) {
      const bone = new Bone()
      const offset = i === 0 ? 0 : is_y ? -sizes[i - 1] : sizes[i - 1]
      bone.position[is_y ? "y" : "x"] = offset
      bones.push(bone)
      if (i > 0) bones[i - 1].add(bone)
    }
    // Bones draw nothing, but the render and each shadow pass recurse through
    // the whole nested chain of every sheet. An invisible root prunes that walk;
    // matrix updates and skinning don't look at `visible`.
    bones[0].visible = false
    return bones
  }

  // Add skinning attributes to geometry based on segment boundaries
  const add_skinning_attributes = (
    geometry: BoxGeometryType,
    boundaries: number[],
    is_y: boolean
  ) => {
    const position = geometry.attributes.position
    const vertex = new Vector3()
    const skin_indexes: number[] = []
    const skin_weights: number[] = []
    const num_segments = boundaries.length - 1

    for (let i = 0; i < position.count; i++) {
      vertex.fromBufferAttribute(position, i)
      const coord = is_y ? -vertex.y : vertex.x

      // Find which bone owns this vertex
      let bone_index = 0
      for (let j = 0; j < num_segments; j++) {
        if (coord >= boundaries[j + 1]) {
          bone_index = j + 1
        }
      }
      // Clamp to valid range
      bone_index = Min(bone_index, num_segments - 1)

      skin_indexes.push(bone_index, 0, 0, 0)
      skin_weights.push(1, 0, 0, 0)
    }

    geometry.setAttribute("skinIndex", new Uint16BufferAttribute(skin_indexes, 4))
    geometry.setAttribute("skinWeight", new Float32BufferAttribute(skin_weights, 4))
  }

  const rgb_to_three = (rgb: RGBColor) => new Color(rgb.red, rgb.green, rgb.blue)

  // One white texel for every empty layer slot of every page: a shared texture
  // lets the renderer skip rebinding the 8 sampler units between pages.
  const placeholder = new DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1)
  placeholder.needsUpdate = true

  // Material slots after merge_edge_groups: edges, front, back
  const FRONT = 1
  const BACK = 2

  // BoxGeometry emits six groups in order +x, -x, +y, -y, +z, -z, each a draw
  // call (plus one per shadow-casting light). The four edges share a material
  // and are contiguous in the index buffer, so fold them into one group: three
  // draws per sheet instead of six. A sheet with no printed faces in view
  // (see show_printed_faces) draws whole with the edge paper: a single draw.
  const merge_edge_groups = (geometry: BoxGeometryType) => {
    const [px, , , , pz, nz] = geometry.groups
    geometry.clearGroups()
    geometry.addGroup(px.start, pz.start - px.start, 0)
    geometry.addGroup(pz.start, pz.count, FRONT)
    geometry.addGroup(nz.start, nz.count, BACK)
    geometry.userData.printed_groups = geometry.groups
    geometry.userData.paper_groups = [
      { count: nz.start + nz.count - px.start, materialIndex: 0, start: px.start },
    ]
  }

  // The parts as one geometry, their groups in a row: the printed ones
  // each part's, the paper one over all
  const merge_parts = (parts: BufferGeometryType[]) => {
    const geometry = new BufferGeometry()
    for (const name of ["position", "normal", "uv", "skinIndex", "skinWeight"]) {
      const first = parts[0]!.attributes[name]!
      const values = parts.flatMap(part => [...part.attributes[name]!.array])
      geometry.setAttribute(
        name,
        name === "skinIndex"
          ? new Uint16BufferAttribute(values, first.itemSize)
          : new Float32BufferAttribute(values, first.itemSize)
      )
    }
    let vertices = 0
    let indices = 0
    const index: number[] = []
    const printed: { start: number; count: number; materialIndex: number }[] = []
    for (const part of parts) {
      index.push(...[...part.index!.array].map(i => i + vertices))
      for (const group of part.groups)
        printed.push({
          ...group,
          start: group.start + indices,
          materialIndex: group.materialIndex!,
        })
      vertices += part.attributes.position!.count
      indices += part.index!.count
    }
    geometry.setIndex(index)
    geometry.userData.printed_groups = printed
    geometry.userData.paper_groups = [{ count: indices, materialIndex: 0, start: 0 }]
    geometry.groups = printed
    return geometry
  }

  // Outside the textured window a sheet's faces are bare paper, which the edge
  // material draws identically (same color and roughness).
  const show_printed_faces = (sheet: PageMesh | FoldableMesh, printed: boolean) => {
    const { userData } = sheet.mesh.geometry
    sheet.mesh.geometry.groups = printed ? userData.printed_groups : userData.paper_groups
  }

  const create_box_materials = (paper_color: RGBColor, paper_roughness: number): MaterialType[] => {
    // Printed faces take theirs from the print pool while in view (see borrow_prints)
    const paper = new MeshStandardMaterial({
      color: rgb_to_three(paper_color),
      roughness: paper_roughness,
    })
    return [paper, paper, paper]
  }

  type PrintShaderMaterial = ReturnType<(typeof three)["PrintShader"]>

  const print_of = (sheet: PageMesh, index: number): PrintShaderMaterial | undefined =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((sheet.mesh.material as MaterialType[])[index] as any).wrapper

  // Only sheets in view draw their printed faces (see show_printed_faces), so
  // they borrow print materials from a pool, by size and roughness, instead of
  // owning two each: on WebGPU a new material builds its shader on its first
  // draw, a hitch every time a page flip brings a sheet into view.
  const print_pool = new Map<string, PrintShaderMaterial[]>()
  const pool_key = ({ paper_roughness, size }: PageMesh) =>
    `${size.width}x${size.height}:${paper_roughness}`

  const borrow_prints = (sheet: PageMesh) => {
    const materials = sheet.mesh.material as MaterialType[]
    for (const index of [FRONT, BACK]) {
      if (print_of(sheet, index)) continue
      const print =
        print_pool.get(pool_key(sheet))?.pop() ??
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        three.PrintShader(three as any, sheet.size, {
          paper_color: sheet.paper_color,
          paper_roughness: sheet.paper_roughness,
          placeholder,
        })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(print.material as any).wrapper = print
      const { blue, green, red } = sheet.paper_color
      print.set_paper_color(red, green, blue)
      materials[index] = print.material
    }
  }

  const return_prints = (sheet: PageMesh) => {
    const materials = sheet.mesh.material as MaterialType[]
    for (const index of [FRONT, BACK]) {
      const print = print_of(sheet, index)
      if (!print) continue
      print.clear_layers()
      const pool = print_pool.get(pool_key(sheet)) ?? []
      print_pool.set(pool_key(sheet), pool)
      pool.push(print)
      materials[index] = materials[0]
    }
  }

  // Internal state
  const settings: SettingsValues = RecordMerge(defaults, initialSettings)
  const sheet_meshes: (PageMesh | FoldableMesh)[] = []

  console.log("CreateBook with settings", settings)

  type PageLocation = { sheet_index: number; side: "front" | "back" }
  // A page shows on every sheet a block's runs repeat it on
  const page_to_sheet = new Map<PageNumber, PageLocation[]>()
  let total_pages = 0

  const is_page_visible = (page_index: number): boolean =>
    page_index === page_frame() - 1 || page_index === page_frame() - 2

  const get_visible_sheet_indices = (): Set<number> => {
    const selected = page_frame() - (settings.book.cover ? 0 : 2)

    const indices = new Set([0, sheet_meshes.length - 1, selected])
    for (let i = 1; i <= PRELOADED_PAGES; i++) {
      indices.add(selected - i)
      indices.add(selected + i)
    }
    return indices
  }

  const clear_sheets = () => {
    sheet_meshes.length = 0
    page_to_sheet.clear()
    total_pages = 0
  }

  const dispose_sheets = () => {
    for (const sheet of sheet_meshes) {
      sheet.mesh.removeFromParent()
      sheet.mesh.geometry.dispose()
      return_prints(sheet)
      ;(sheet.mesh.material as MaterialType[])[0].dispose()
      sheet.mesh.skeleton.dispose()
    }
    clear_sheets()
  }

  const register_sheet = (sheet: PageMesh | FoldableMesh) => {
    sheet_meshes.push(sheet)
    const sheet_index = sheet.sheet_index
    const locate = (side: PageLocation["side"]) => {
      total_pages++
      const locations = page_to_sheet.get(total_pages as PageNumber) ?? []
      locations.push({ sheet_index, side })
      page_to_sheet.set(total_pages as PageNumber, locations)
    }
    if (sheet.print_front) locate("front")
    if (sheet.print_back) locate("back")
  }

  const get_cover = (): FoldableMesh => {
    if (!settings.book.cover) throw new Error("Book has no cover")
    return sheet_meshes[0] as FoldableMesh
  }

  let is_animating_to_ideal = true
  let user_interaction_timeout: ReturnType<typeof setTimeout> | undefined

  // On-demand rendering. The scene has no time-based shaders — every animation
  // (page/bone easing, cover fold, camera damping and auto-return, the
  // loading-color pulse) eases toward a target and then holds still. Rendering
  // every frame regardless just pins the GPU (measured ~120fps / 180 draw calls
  // per frame on a static book). So we only draw while something is actually
  // moving, keep a short tail so the final resting frame lands, and wake on any
  // external change (textures, settings, resize) via invalidate().
  const RENDER_SETTLE_FRAMES = 4
  const MOTION_EPS = 1e-4
  let render_countdown = RENDER_SETTLE_FRAMES
  let frame_motion = 0
  const invalidate = () => (render_countdown = RENDER_SETTLE_FRAMES)

  // Easing helper: returns delta to move current toward target, recording the
  // largest step this frame so the loop knows whether the image changed. Every
  // eased property (book, bones, cover, stripe, rotation) goes through here.
  // A step too small to count lands on the target instead: left short, a
  // sheet's bones or a board stay a hair bent, and on a closed book the block
  // pokes through the cover at the fore-edge.
  const ease_delta = (current: number, target: number, ease: number): number => {
    const step = (target - current) * ease
    const delta = !ease || Abs(step) > MOTION_EPS ? step : target - current
    const abs = Abs(delta)
    if (abs > frame_motion) frame_motion = abs
    return delta
  }

  // Which way along a foldable's thickness is in, toward the pages
  // How far the most open of the cover's flaps is (0 without any), 0 to 1, as
  // last frame left them: closing, the boards and the pages wait for it
  let flaps_progress = 0

  // A cover's boards are its step 1, its flaps what opens after: a flap is the
  // front's when it lies past the spine along the opening, the back's before
  const is_front_flap = (folds: FoldCell[], fold: FoldCell): boolean => {
    const along = ({ box }: FoldCell) =>
      get_is_y(settings) ? -(box.y + box.height / 2) : box.x + box.width / 2
    return along(fold) > along(folds[0]!)
  }

  // A cover's flap adds a frame, the book open on it with its side bare: the
  // front's before the first spread, the back's after the last
  const flap_frames = () => {
    const folds = (!is_stripe_animation(settings) && settings.book.cover?.folds) || []
    const flaps = folds.filter(fold => fold.step > 1)
    return {
      back: flaps.some(fold => !is_front_flap(folds, fold)) ? 1 : 0,
      front: flaps.some(fold => is_front_flap(folds, fold)) ? 1 : 0,
    }
  }

  // A book with a cover starts closed; one that never closes on its first
  // page, as a coverless book
  const frame_start = () =>
    is_stripe_animation(settings) ||
    (settings.book.cover && !never_closes()) ||
    get_folding(settings)
      ? 0
      : 1

  const frame_end = () => {
    const sheets = get_total_sheets(settings)
    if (is_stripe_animation(settings)) {
      return sheets - (settings.book.cover ? 0 : 1)
    }

    const folding = get_folding(settings)
    if (folding) return 2 * last_step(folding.folds)
    if (!settings.book.cover && sheets <= 1) return 1
    const { back, front } = flap_frames()
    return sheets + (settings.book.cover ? 1 : 0) + 1 + front + back
  }

  // The frame as the pages count it, past the front flap's
  const page_frame = () => settings.frame_selected - flap_frames().front

  // The flap the frame shows open, if any
  const flap_open = (): "front" | "back" | undefined => {
    const { back, front } = flap_frames()
    if (front && settings.frame_selected === frame_start() + 1) return "front"
    if (back && settings.frame_selected === frame_end() - 1) return "back"
    return undefined
  }

  // A page folding is never a book open: it is seen from the front
  const is_book_opened = () =>
    !get_folding(settings) &&
    (never_closes() ||
      !(settings.frame_selected <= frame_start() || settings.frame_selected >= frame_end()))

  // Scene setup
  const scene = new Scene()
  scene.background = null

  renderer.setSize(container.clientWidth, container.clientHeight)
  renderer.setPixelRatio(Min(window.devicePixelRatio, 2))
  if ("shadowMap" in renderer) renderer.shadowMap.enabled = true
  container.appendChild(renderer.domElement)

  const create_page_geometry = (
    width: number,
    height: number,
    depth: number,
    bone_sizes: number[]
  ) => {
    const is_y = get_is_y(settings)
    const segments = bone_sizes.length
    const geometry = is_y
      ? new BoxGeometry(width, height, depth, 2, segments)
      : new BoxGeometry(width, height, depth, segments, 2)
    geometry.translate(is_y ? 0 : width / 2, is_y ? -height / 2 : 0, 0)

    // Build cumulative segment boundaries
    const boundaries = bone_sizes.reduce<number[]>(
      (acc, size) => [...acc, acc[acc.length - 1] + size],
      [0]
    )
    add_skinning_attributes(geometry, boundaries, is_y)
    merge_edge_groups(geometry)
    geometry.computeBoundingSphere()
    geometry.computeBoundingBox()

    return geometry
  }

  const create_page_mesh = (
    sheet_index: number,
    depth: number,
    span: number,
    bone_sizes: number[],
    paper_roughness: number,
    paper_color: RGBColor,
    print_front: boolean,
    print_back: boolean
  ): PageMesh => {
    const is_y = get_is_y(settings)
    const bones = create_bone_chain(bone_sizes, is_y)
    const skeleton = new Skeleton(bones)

    const bone_span = Total(...bone_sizes)
    const width = (is_y ? span : bone_span) as Distance
    const height = (is_y ? bone_span : span) as Distance

    const materials = create_box_materials(paper_color, paper_roughness)
    const geometry = create_page_geometry(width, height, depth, bone_sizes)
    const mesh = new SkinnedMesh(geometry, materials)
    mesh.frustumCulled = false
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.add(skeleton.bones[0])
    mesh.bind(skeleton)
    // A frozen sheet (see update_pages) keeps last frame's bone matrices; the
    // renderer would otherwise recompute and re-upload them every frame.
    const update_skeleton = skeleton.update.bind(skeleton)
    skeleton.update = () => {
      if (mesh.matrixWorldAutoUpdate) update_skeleton()
    }

    return {
      bones,
      mesh,
      paper_color,
      paper_roughness,
      print_back,
      print_front,
      sheet_index,
      size: {
        depth: depth as Distance,
        height: height as Distance,
        width: width as Distance,
      },
    }
  }

  // Sheets whose matrices must be recomputed regardless of motion (new meshes)
  let sheets_rebuilt = true
  // A book just built shows where it is, not how it got there: its first frame
  // jumps every eased value to its target, stepping snap_steps times so the
  // cover's stages (boards, flaps, pages) each settle in turn
  const snap_steps = () =>
    3 + last_step([...(settings.book.cover?.folds ?? []), ...(get_folding(settings)?.folds ?? [])])
  let snap = true
  const ease_of = (delta: number) => (snap ? 1 : settings.easing_factor * delta * 10)

  const update_pages = (delta: number, group_moved: boolean) => {
    const top_page_shift_molt = 0.1
    const top_page_side_molt = 0.01

    const rotation_axis = get_rotation_axis(settings)
    const start_index = settings.book.cover ? 1 : 0
    // Every sheet stacks in its slot, its share of its block, the gap of the
    // slot it doesn't fill split either side of it
    const total_depth = Total(...settings.book.blocks.map(block => block.size.depth)) as Distance
    // Closing, the block lies until the flaps are halfway closed
    const book_opened = is_book_opened() || flaps_progress > 0.5
    const ease = ease_of(delta)
    const is_y = get_is_y(settings)

    let cumulative_depth = 0
    let sheet_index = start_index

    for (const block of settings.book.blocks) {
      const bone_span = is_y ? block.size.height : block.size.width
      const segment_length = (bone_span / settings.page_segments) as Distance
      const slot = block.size.depth / block.sheets
      // The page at the opening lifts off the rest, even on a thin book
      const top_shift = Max(
        total_depth * top_page_shift_molt,
        Max(block.size.width, block.size.height) * top_page_side_molt
      )

      const min_segments = Max(
        CurvatureMinSegments(segment_length, (total_depth + top_shift) as Distance),
        settings.opening_segments
      )

      for (let i = 0; i < block.sheets; i++) {
        const page_mesh = sheet_meshes[sheet_index] as PageMesh | undefined
        if (!page_mesh) break

        const page_index = page_mesh.sheet_index - (settings.book.cover ? 1 : 0)

        // Book mode behavior
        // frame_selected is 1-based, sheet_index is 0-based
        // frame_selected=1 means showing page 1, which is index 0, so no pages are "opened" (flipped back)
        const opened = book_opened && page_frame() > page_index + 1

        // Center offset for positioning
        const centered_depth = cumulative_depth - total_depth / 2

        // Elevation distributes pages on a semicircle (only when opened)
        // angle goes from 0 (first page) to PI (last page)
        const angle = ((centered_depth / total_depth) * PI) as Radian
        const radius = total_depth / 2
        const elevation_x = book_opened ? Cos(angle) * radius * settings.page_elevation : 0

        // Avoid division by zero for single-page books

        const bones = page_mesh.mesh.skeleton.bones
        // Track this sheet's own motion; restored into the frame max below
        const motion_before = frame_motion
        frame_motion = 0

        // Top page shift - pages at the opening get extra offset
        const is_top_page = is_page_visible(page_index)
        const top_page_shift = book_opened && is_top_page ? top_shift : 0

        // Calculate curvature angles based on page position
        // opened pages (left side) use cumulative_depth, closed pages (right side) use remaining depth
        const target_altitude = opened ? cumulative_depth : total_depth - cumulative_depth

        const curvature_angles = [
          ...CurvatureAngles(
            segment_length,
            (target_altitude + top_page_shift - elevation_x) as Distance,
            min_segments,
            page_half()
          ),
        ]

        for (let j = 0; j < bones.length; j++) {
          const bone = bones[j]
          let rotation_angle = 0

          if (book_opened && j <= min_segments) {
            // Use curvature angles for the curve (only when book is open)
            const curvature_angle = curvature_angles[j] ?? 0
            rotation_angle = curvature_angle * (opened ? 1 : -1)
          }

          // Most bones sit flat (target 0, already 0): skip the write, every
          // rotation assignment recomputes the bone quaternion
          const bone_delta = ease_delta(bone.rotation[rotation_axis], rotation_angle, ease)
          if (bone_delta !== 0) bone.rotation[rotation_axis] += bone_delta
        }

        // Reset x position for book mode, use cumulative depth for z stacking
        page_mesh.mesh.position.x += ease_delta(
          page_mesh.mesh.position.x,
          settings.book.blocks_offset.x + elevation_x,
          ease
        )
        page_mesh.mesh.position.y += ease_delta(
          page_mesh.mesh.position.y,
          settings.book.blocks_offset.y,
          ease
        )
        const z = -centered_depth - slot / 2
        const moving = frame_motion > MOTION_EPS
        // During a flip only a few sheets move, yet three recomputes every
        // bone's world matrix and skeleton each frame (~30 bones per sheet).
        // A sheet that didn't move keeps last frame's matrices: freeze it.
        // Sub-threshold easing is left pending, as the idle loop leaves it.
        page_mesh.mesh.matrixWorldAutoUpdate =
          moving || group_moved || sheets_rebuilt || page_mesh.mesh.position.z !== z
        page_mesh.mesh.position.z = z
        frame_motion = Max(frame_motion, motion_before)

        cumulative_depth += slot
        sheet_index++
      }
    }
    sheets_rebuilt = false
  }

  const flip_camera = (v: Vector3Type): Vector3Type => (settings.camera_flip ? v.negate() : v)

  // A page folding is seen from the front closed, and from its inside, its
  // rear, once it opens, to its back folded again: as a book shows its
  // spreads and its back cover
  const folding_inside = () => settings.frame_selected > frame_start()

  // How far each half of the open pages turns from the closed book: a flat
  // spread's a quarter turn; past it, over the top, a desk calendar's
  const page_half = () => ((settings.book.page_opening ?? PI) / 2) as Radian

  // Pages turning over the top never lie closed: a desk calendar stands
  const never_closes = () => page_half() > PI / 2

  // Closed, whether its back shows: a single sheet always shows its front
  const past_middle = () => {
    const total_sheets = get_total_sheets(settings)
    return get_folding(settings)
      ? folding_inside()
      : total_sheets > 1 && page_frame() > total_sheets / 2
  }

  // The view on the open book: square on the half not yet turned, its front
  // turned with it — a flat spread's, or a desk calendar's front face. The
  // closed pages lie out from the binding along x, their front at z
  const opened_camera_direction = (): Vector3Type => {
    const half = page_half()
    return flip_camera(new Vector3(Sin(half), 0, Cos(half)))
  }

  // Opening at the top, the book stands as a closed one does, its binding
  // along x up top, and turns on it while the camera stays: none closed,
  // open the half not yet turned square to the camera, turned over the top
  // past the middle, its back to the camera
  const top_turn = (): number => {
    if (is_book_opened()) return -page_half()
    return past_middle() ? -PI : 0
  }

  const get_ideal_camera_direction = (): Vector3Type => {
    // Stripe mode: camera looks down at pages from above; a book opening at
    // the top turns itself to the camera
    if (is_stripe_animation(settings) || get_is_y(settings))
      return flip_camera(new Vector3(0, 0, 1))
    if (!is_book_opened()) return flip_camera(new Vector3(0, 0, past_middle() ? -1 : 1))
    return opened_camera_direction()
  }

  // Up is always up: the book turns, the camera orbits round y
  const get_ideal_camera_up = (): Vector3Type => new Vector3(0, 1, 0)

  const get_selected_stripe_item = (): PageMesh | FoldableMesh => {
    if (!is_stripe_animation(settings)) {
      throw new Error("get_selected_stripe_item called outside stripe mode")
    }

    const index = Min(settings.frame_selected, frame_end())
    return sheet_meshes[index]
  }

  const get_ideal_camera_target = (): Vector3Type => {
    const is_y = get_is_y(settings)

    // A page folding is looked at in the middle of what it covers now
    const folding = get_folding(settings) && !is_stripe_animation(settings) && sheet_meshes[0]
    if (folding && "folds" in folding) {
      const corners = folding_corners(folding)
      const low = corners.reduce((a, b) => a.min(b), corners[0]!.clone())
      const high = corners.reduce((a, b) => a.max(b), corners[0]!.clone())
      return low.add(high).multiplyScalar(0.5)
    }

    if (is_stripe_animation(settings)) {
      const selected_item = get_selected_stripe_item()
      return new Vector3(
        selected_item.mesh.position.x + (is_y ? 0 : selected_item.size.width / 2),
        selected_item.mesh.position.y + (is_y ? -selected_item.size.height / 2 : 0),
        0
      )
    }

    // Closed, the middle of the pages; open, the middle of the pages' height:
    // the binding on a flat spread, halfway down a desk calendar
    const { height, width } = get_book_dimensions(settings)
    const reach = is_book_opened() ? Cos(page_half()) : 1
    if (is_y)
      return new Vector3(0, (-height / 2) * reach, 0).applyAxisAngle(
        new Vector3(1, 0, 0),
        top_turn()
      )
    return new Vector3((width / 2) * reach, 0, 0)
  }

  const update_view_pointer = () => {
    const visible = !!settings.camera_debug_arrow
    view_pointer.visible = visible
    origin_marker.visible = visible
    for (const info of spot_lights) {
      info.marker.visible = visible
    }

    if (!visible) return

    const pos = get_ideal_camera_target()
    const dir = get_ideal_camera_direction()

    view_pointer.position.copy(pos)
    view_pointer.setDirection(dir)
  }

  const camera_auto_return = () => {
    const target_distance = get_ideal_camera_zoom()
    const current_distance = camera.position.distanceTo(controls.target)

    // Always animate zoom level changes
    const new_distance =
      current_distance + (target_distance - current_distance) * settings.camera_return_speed
    const current_direction = camera.position.clone().sub(controls.target).normalize()

    const ideal_target = get_ideal_camera_target()

    // Always move target to ideal
    controls.target.lerp(ideal_target, settings.camera_return_speed)

    if (is_animating_to_ideal && settings.camera_auto_return) {
      const ideal_direction = get_ideal_camera_direction()
      // Move at constant angular speed instead of easing
      const step = settings.camera_return_speed
      const dot = current_direction.dot(ideal_direction)
      let new_direction: Vector3Type
      if (dot < -0.9) {
        // Nearly opposite - turn through the open book's view instead of lerping
        // through zero, the same way front to back as back to front; round
        // the side for a book opening at the top, its view never turns
        const opened = get_is_y(settings) ? new Vector3(1, 0, 0) : opened_camera_direction()
        const perpendicular = opened
          .sub(current_direction.clone().multiplyScalar(opened.dot(current_direction)))
          .normalize()
        new_direction = current_direction
          .clone()
          .add(perpendicular.multiplyScalar(step * 2))
          .normalize()
      } else {
        new_direction = current_direction
          .lerp(ideal_direction, settings.camera_return_speed)
          .normalize()
      }
      camera.position.copy(controls.target).add(new_direction.multiplyScalar(new_distance))
    } else {
      // Just adjust distance, keep direction
      camera.position.copy(controls.target).add(current_direction.multiplyScalar(new_distance))
    }

    // Update spotlights - positioned relative to ideal camera orientation, pointing at ideal target
    // Calculate light distance so spotlight cone covers the visible area
    const light_distance = target_distance * 1.5
    const light_intensity = settings.ambient_only ? 0 : 8 * light_distance ** 2 // 200 * (light_distance / 5) ** 2

    const ideal_up = get_ideal_camera_up()
    const ideal_dir = get_ideal_camera_direction()
    const ideal_right = new Vector3().crossVectors(ideal_up, ideal_dir).normalize()

    const marker_scale = target_distance * 0.05

    for (const { light, marker, unit_offset } of spot_lights) {
      // unit_offset: x=up, y=right, z=forward in camera-relative coordinates (normalized)
      light.position
        .copy(ideal_target)
        .add(ideal_up.clone().multiplyScalar(unit_offset.x * light_distance))
        .add(ideal_right.clone().multiplyScalar(unit_offset.y * light_distance))
        .add(ideal_dir.clone().multiplyScalar(unit_offset.z * light_distance))
      light.target.position.copy(ideal_target)
      light.intensity = light_intensity
      light.distance = light_distance * 3 // needed for shadow camera far plane
      marker.position.copy(light.position)
      marker.scale.setScalar(marker_scale)
      marker.lookAt(ideal_target)
    }
  }

  const update_cover = (delta: number) => {
    if (!settings.book.cover || !sheet_meshes[0]) {
      flaps_progress = 0
      return
    }

    const ease = ease_of(delta)
    const cover = get_cover()
    flaps_progress = Max(
      0,
      ...cover.folds.flatMap((fold, i) => (fold.step > 1 ? [open_of(cover, i)] : []))
    )
    place_foldable(cover, ease)
    update_foldable(cover, ease, cover_targets(cover))
  }

  function* get_stripe_items(): Generator<PageMesh | FoldableMesh> {
    yield* sheet_meshes
  }

  const update_stripe = (delta: number) => {
    const ease = ease_of(delta)

    // Position items starting from origin
    let current_x = 0

    const rotation_axis = get_rotation_axis(settings)
    for (const item of get_stripe_items()) {
      item.mesh.matrixWorldAutoUpdate = true
      // Flatten all bones
      if ("folds" in item) update_foldable(item, ease, [...Repeat(0, item.folds.length)])
      else
        for (const bone of item.bones) {
          bone.rotation[rotation_axis] += ease_delta(bone.rotation[rotation_axis], 0, ease)
        }

      // Flatten mesh rotation
      item.mesh.rotation.x += ease_delta(item.mesh.rotation.x, 0, ease)
      item.mesh.rotation.y += ease_delta(item.mesh.rotation.y, 0, ease)
      item.mesh.rotation.z += ease_delta(item.mesh.rotation.z, 0, ease)

      item.mesh.position.x += ease_delta(item.mesh.position.x, current_x, ease)
      item.mesh.position.y += ease_delta(item.mesh.position.y, item.size.height / 2, ease)
      item.mesh.position.z += ease_delta(item.mesh.position.z, 0, ease)

      current_x += item.size.width * settings.pile_distance
    }
  }

  const rebuild_from_book = () => {
    dispose_sheets()
    sheets_rebuilt = true
    snap = true

    const is_y = get_is_y(settings)

    // The cover, folding round the block from its spine, which lies on the
    // block's back, the block's middle at the spine's middle — as far past
    // the binding as a board reaches turned as the pages are: none on a flat
    // spread, a desk calendar's base under the pages hung over its boards
    const cover = settings.book.cover
    if (cover) {
      const [spine, board] = cover.folds
      const middle = spine!.box.x + spine!.box.width / 2
      const center = spine!.box.y + spine!.box.height / 2
      const below = board ? -(is_y ? board.box.height : board.box.width) * Cos(page_half()) : 0
      const cover_mesh = create_foldable(
        cover,
        0,
        // opening at the top, laid back to front: a quarter turn back on x
        // stands the spine on the binding, the front board on the block's front
        is_y
          ? new Vector3(cover.size.width - 2 * middle, below, center - cover.size.height)
          : new Vector3(-cover.size.depth - below, 0, -middle),
        is_y ? new Euler(-PI / 2, 0, 0) : new Euler(0, -PI / 2, 0)
      )
      register_sheet(cover_mesh)
      // turning with the block
      book_group.add(cover_mesh.mesh)
    }

    // A page folding, where a page would lie
    const folding = get_folding(settings)
    if (folding) {
      const folding_mesh = create_foldable(folding, 0, new Vector3(), new Euler())
      register_sheet(folding_mesh)
      book_group.add(folding_mesh.mesh)
      place_textures()
      apply_visible_textures()
      return
    }

    // Create page meshes
    let sheet_index = cover ? 1 : 0
    for (const block of settings.book.blocks) {
      const page_depth = (block.size.depth / block.sheets) * settings.sheet_fill
      const bone_span = is_y ? block.size.height : block.size.width
      const other_span = is_y ? block.size.width : block.size.height
      const bone_sizes = [...Repeat(bone_span / settings.page_segments, settings.page_segments)]
      // Each run of the block's sheets shows its pages again, from its first
      const run = block.sheets / block.copies
      const first_page = total_pages

      for (let i = 0; i < block.sheets; i++) {
        if (i % run === 0) total_pages = first_page
        const page_mesh = create_page_mesh(
          sheet_index,
          page_depth,
          other_span,
          bone_sizes,
          block.paper_roughness,
          block.paper_color,
          block.print_front,
          block.print_rear
        )
        register_sheet(page_mesh)
        book_group.add(page_mesh.mesh)
        sheet_index++
      }
    }

    // Pages moved to other sheets: place the textures again, then reapply
    // the visible ones
    place_textures()
    apply_visible_textures()
  }

  // Animation loop
  const ease_book_rotation = (delta: number) => {
    const ease = ease_of(delta)
    const is_y = get_is_y(settings)
    const rotation_axis = get_rotation_axis(settings)

    if (is_stripe_animation(settings)) {
      book_group.rotation[rotation_axis] += ease_delta(book_group.rotation[rotation_axis], 0, ease)
      book_group.rotation[get_pos_axis(settings)] += ease_delta(
        book_group.rotation[get_pos_axis(settings)],
        0,
        ease
      )
      return
    }

    const target_rotation = is_y ? top_turn() : 0
    book_group.rotation[get_pos_axis(settings)] = 0
    book_group.rotation[rotation_axis] += ease_delta(
      book_group.rotation[rotation_axis],
      target_rotation,
      ease
    )
  }

  // The group matrix is updated by hand (matrixAutoUpdate off): any update
  // forces every sheet and bone below it to recompute, defeating the per-sheet
  // freeze. Returns whether it moved; sub-threshold steps are dropped.
  const previous_rotation = new Euler()
  const update_book_rotation = (delta: number): boolean => {
    previous_rotation.copy(book_group.rotation)
    ease_book_rotation(delta)
    const moved =
      Abs(book_group.rotation.x - previous_rotation.x) > MOTION_EPS ||
      Abs(book_group.rotation.y - previous_rotation.y) > MOTION_EPS ||
      Abs(book_group.rotation.z - previous_rotation.z) > MOTION_EPS
    if (moved) book_group.updateMatrix()
    else book_group.rotation.copy(previous_rotation)
    return moved
  }

  const timer = new Timer()
  let animation_id = 0
  // Previous-frame camera transform — camera motion (damping, orbit,
  // auto-return) doesn't go through ease_delta, so it's detected by comparison.
  const prev_cam_pos = new Vector3()
  const prev_cam_target = new Vector3()
  const prev_cam_up = new Vector3()
  const animate = () => {
    animation_id = requestAnimationFrame(animate)

    // Keep the timer ticking every frame so getDelta stays a single frame's
    // worth even across idle stretches — otherwise the first frame after waking
    // would ease with a huge accumulated delta and overshoot.
    timer.update()

    // Fully idle: nothing is animating and nothing external woke us. Skip the
    // whole update+render pipeline — this is the CPU win, since update_pages
    // alone walks every sheet (256+ in a big book) doing curvature math and
    // allocations each frame. Wake sources call invalidate() to resume.
    if (render_countdown <= 0) return

    const delta = timer.getDelta()

    frame_motion = 0
    controls.update()
    update_view_pointer()
    camera_auto_return()
    for (let step = 0, steps = snap ? snap_steps() : 1; step < steps; step++) {
      const group_moved = update_book_rotation(delta)
      if (is_stripe_animation(settings)) {
        // Unified stripe mode for cover and pages
        update_stripe(delta)
      } else if (get_folding(settings)) {
        const folding = sheet_meshes[0] as FoldableMesh | undefined
        if (folding) {
          place_foldable(folding, ease_of(delta))
          update_foldable(folding, ease_of(delta), folding_targets(folding))
        }
      } else {
        // Book mode
        update_pages(delta, group_moved || snap)
        update_cover(delta)
      }
    }
    snap = false

    const pulsing = pulse_visible_pages((Sin((timer.getElapsed() * 3 + 2) as Radian) + 1) / 2)

    // Camera transform delta since last frame, normalized by view distance so
    // the threshold is scene-scale independent (roughly angular).
    const cam_dist = Max(camera.position.distanceTo(controls.target), 1e-6)
    const cam_pos_d = camera.position.distanceTo(prev_cam_pos) / cam_dist
    const cam_tgt_d = controls.target.distanceTo(prev_cam_target) / cam_dist
    const cam_up_d = 1 - Min(1, camera.up.dot(prev_cam_up))
    const cam_moved = cam_pos_d > MOTION_EPS || cam_tgt_d > MOTION_EPS || cam_up_d > MOTION_EPS
    prev_cam_pos.copy(camera.position)
    prev_cam_target.copy(controls.target)
    prev_cam_up.copy(camera.up)

    // Motion this frame keeps the loop awake; when it stops, the countdown
    // drains over a few frames (landing the resting frame) and then idles.
    if (frame_motion > MOTION_EPS || cam_moved || pulsing) invalidate()

    renderer.render(scene, camera)
    render_countdown--
  }

  // Apply textures for pages currently in view. Everything else is dropped:
  // a rasterized page is ~16 MB of GPU memory (2048px RGBA + mipmaps), so
  // keeping every page ever shown grows past a GB within a few dozen flips.
  // Dropped pages are requested again when navigated back to.
  const apply_visible_textures = () => {
    const visible = get_visible_sheet_indices()
    const needed = new Set<string>()
    // Sheets leaving the view hand their prints back before others borrow
    for (const [sheet_index, sheet] of sheet_meshes.entries()) {
      show_printed_faces(sheet, visible.has(sheet_index))
      if (!visible.has(sheet_index)) return_prints(sheet)
    }
    for (const sheet_index of visible) {
      const sheet = sheet_meshes[sheet_index]
      if (!sheet) continue
      borrow_prints(sheet)
      const pending = page_materials.get(sheet_index)
      for (const { texture } of [...(pending?.front ?? []), ...(pending?.back ?? [])]) {
        needed.add(RasterKey(texture))
      }
      void apply_textures(sheet_index)
    }
    for (const [key, loading] of texture_map) {
      if (needed.has(key)) continue
      texture_map.delete(key)
      unavailable.delete(key)
      drop_texture(loading)
    }
  }

  // Public API methods
  const update_model = (updated_settings: Partial<SettingsValues>) => {
    const segments_changed =
      HasValue(updated_settings.page_segments) || HasValue(updated_settings.sheet_fill)
    const book_changed = HasValue(updated_settings.book)
    const frame_changed = HasValue(updated_settings.frame_selected)

    // Reset current visible pages to paper color before frame changes
    if (frame_changed) pulse_visible_pages()

    // Update settings
    RecordAssign(settings, updated_settings)

    if (book_changed || segments_changed) {
      rebuild_from_book()

      // Update camera near/far planes based on new book dimensions
      const { height, width } = get_book_dimensions(settings)
      const max_dimension = Max(height, width)
      camera.near = max_dimension * 0.01
      camera.far = max_dimension * 100
      camera.updateProjectionMatrix()
    }

    // Handle lighting changes
    if (HasValue(updated_settings.ambient_only)) update_lighting()

    // Apply textures for visible pages when frame changes
    if (frame_changed) apply_visible_textures()

    // Any settings change may alter the image (frame, book, lighting, zoom,
    // print/guide toggles) without necessarily moving geometry — wake the loop.
    invalidate()

    // Zoom level changes handled by camera_auto_return in animation loop
  }

  const max_anisotropy =
    "capabilities" in renderer
      ? renderer.capabilities.getMaxAnisotropy()
      : renderer.getMaxAnisotropy()

  const bitmap_loader = new three.ImageBitmapLoader()
  // Decode exactly as WebGL uploads (no premultiply, no color conversion):
  // otherwise texImage2D converts the whole bitmap on the main thread first.
  bitmap_loader.setOptions({
    colorSpaceConversion: "none",
    imageOrientation: "flipY",
    premultiplyAlpha: "none",
  })

  const load_texture = (url: string): Promise<Texture> =>
    new Promise((resolve, reject) =>
      bitmap_loader.load(
        url,
        (bitmap: ImageBitmap) => {
          const texture = new three.CanvasTexture(bitmap)
          // The bitmap is already flipped (imageOrientation above): WebGL
          // can't flip an ImageBitmap on upload anyway, WebGPU can and would
          // flip it back
          texture.flipY = false
          texture.colorSpace = SRGBColorSpace
          // Pages are mostly seen at an angle (curving, turning, orbiting):
          // without anisotropic filtering mipmapping blurs them there
          texture.anisotropy = max_anisotropy
          resolve(texture)
        },
        undefined,
        reject
      )
    )

  // What set_textures was last handed, and where it lands: by sheet and side
  let book_textures: BookTexture[] = []
  const page_materials = new Map<number, SheetTextures>()
  // By RasterKey. Promises, so concurrent requests for one raster share a
  // single request and load; undefined where there was no raster to load
  const texture_map = new Map<string, Promise<Texture | undefined>>()
  // The ones that came back undefined: nothing more is coming for them until
  // they leave the view and are asked for again
  const unavailable = new Set<string>()

  // Frees GPU memory and the decoded bitmap; a failed load has nothing to free
  // (its error already surfaced to whoever awaited it)
  const drop_texture = (loading: Promise<Texture | undefined>) =>
    void loading.then(texture => {
      texture?.dispose()
      ;(texture?.image as ImageBitmap | undefined)?.close()
    }, Void)

  // Returns true while actively pulsing (a texture is on its way but not yet
  // loaded) — a time-based animation the render loop must keep drawing.
  // `awaiting` guards against pulsing forever: get_visible_sheet_indices always
  // includes the first and last sheet for preloading, which may show nothing,
  // so without this they'd shimmer (and pin the GPU) indefinitely. A side with
  // no texture just holds paper color.
  const pulse_side = (
    material: MaterialType,
    pulse: number,
    paper_color: RGBColor,
    loading_color: RGBColor,
    awaiting: boolean
  ): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrapper = (material as any).wrapper as ReturnType<(typeof three)["PrintShader"]>
    if (!wrapper) return false
    if (wrapper.has_layer() || !awaiting) {
      // Loaded, or nothing coming - hold at paper color.
      wrapper.set_paper_color(paper_color.red, paper_color.green, paper_color.blue)
      return false
    }
    // Texture pending - pulse between loading color and paper color.
    const pc = paper_color
    const lc = loading_color
    wrapper.set_paper_color(
      lc.red + (pc.red - lc.red) * pulse,
      lc.green + (pc.green - lc.green) * pulse,
      lc.blue + (pc.blue - lc.blue) * pulse
    )
    return true
  }

  // A face still has something coming while any of its textures may yet load
  const is_awaiting = (textures: BookTexture[] | undefined): boolean =>
    !!textures?.some(({ texture }) => !unavailable.has(RasterKey(texture)))

  const pulse_visible_pages = (pulse = 1): boolean => {
    let pulsing = false
    for (const sheet_index of get_visible_sheet_indices()) {
      const item = sheet_meshes[sheet_index]
      if (!item) continue

      const pending = page_materials.get(sheet_index)
      const materials = item.mesh.material as MaterialType[]

      if (item.print_front) {
        const awaiting = is_awaiting(pending?.front)
        pulsing =
          pulse_side(materials[FRONT], pulse, item.paper_color, settings.loading_color, awaiting) ||
          pulsing
      }
      if (item.print_back) {
        const awaiting = is_awaiting(pending?.back)
        pulsing =
          pulse_side(materials[BACK], pulse, item.paper_color, settings.loading_color, awaiting) ||
          pulsing
      }
    }
    return pulsing
  }

  const get_or_load_texture = (texture: PageTextureLayer): Promise<Texture | undefined> => {
    const key = RasterKey(texture)
    const cached = texture_map.get(key)
    if (cached) return cached
    // Never rejects: a page with no raster, or one that would not load, is
    // undefined, and the face stops waiting for it
    const loading = request_raster(texture.filename, texture.page)
      .then(url => (url ? load_texture(url) : undefined))
      .catch(() => undefined)
      .then(loaded => {
        if (!loaded && texture_map.get(key) === loading) unavailable.add(key)
        return loaded
      })
    texture_map.set(key, loading)
    return loading
  }

  const apply_textures = async (sheet_index: number) => {
    const pending = page_materials.get(sheet_index)
    if (!pending) return

    const target = sheet_meshes[sheet_index]
    if (!target) return

    const apply_side = (side: "front" | "back") => {
      const index = side === "front" ? FRONT : BACK
      const dynamic_material = print_of(target, index)
      if (!dynamic_material) return []

      // All layers load in parallel; add the ones that don't exist yet
      return pending[side].map(async ({ texture }) => {
        if (dynamic_material.has_layer(texture.order)) return
        const loading = get_or_load_texture(texture)
        const loaded = await loading
        // No raster for it, or evicted while loading (navigated away): its
        // bitmap is already closed. Gone back to the pool meanwhile: the
        // print may already be another sheet's
        if (!loaded || texture_map.get(RasterKey(texture)) !== loading) return
        if (print_of(target, index) !== dynamic_material) return
        dynamic_material.add_layer({ ...texture, texture: loaded })
        // A texture arrived — the page's appearance changed with no geometry
        // motion, so the loop wouldn't otherwise redraw it.
        invalidate()
      })
    }

    await WaitPromises([...apply_side("front"), ...apply_side("back")])
  }

  // Sort the textures onto the sheet and side their page is on
  const place_textures = () => {
    page_materials.clear()
    for (const item of book_textures) {
      for (const location of page_to_sheet.get(item.page_number) ?? []) {
        const sheet = page_materials.get(location.sheet_index) ?? { back: [], front: [] }
        page_materials.set(location.sheet_index, sheet)
        sheet[location.side].push(item)
      }
    }
  }

  // Every printed face goes back to paper and takes its layers again: a page
  // may now show another file's page. The rasters still in view are held
  // (see texture_map), so what did not change is back before the next frame.
  const set_textures = (textures: BookTexture[]) => {
    book_textures = textures
    for (const sheet of sheet_meshes) {
      for (const index of [FRONT, BACK]) print_of(sheet, index)?.clear_layers()
    }
    place_textures()
    apply_visible_textures()
    invalidate()
  }

  const dispose = () => {
    cancelAnimationFrame(animation_id)

    // Dispose cached textures
    for (const [, loading] of texture_map) drop_texture(loading)
    page_materials.clear()
    texture_map.clear()

    dispose_sheets()
    for (const pool of print_pool.values()) for (const { material } of pool) material.dispose()
    print_pool.clear()

    // Clean up scene
    scene.remove(book_group)
    book_group.clear()

    // Dispose lights
    ambient_light.dispose()
    for (const { light, marker } of spot_lights) {
      light.dispose()
      marker.geometry.dispose()
      const mats = marker.material as MaterialType[]
      for (const mat of mats) mat.dispose()
    }
    placeholder.dispose()

    // Dispose helpers
    view_pointer.dispose()
    origin_marker.dispose()

    // Dispose renderer
    resize_observer.disconnect()
    void renderer.dispose()
    container.removeChild(renderer.domElement)
  }

  // Initialize book group
  const book_group = new Mesh()
  book_group.visible = true
  // Neither ever updates on its own: an auto-updated parent forces its whole
  // subtree to recompute every frame (see update_book_rotation / update_pages)
  book_group.matrixAutoUpdate = false
  scene.matrixAutoUpdate = false

  // A sheet that folds, laid out as a page is: from 0 along x, centered on
  // y, or from 0 down y, centered on x, when it opens at the top. The box is
  // cut on every cell's edges, each vertex moved by its cell's bone; a bone
  // turns on the edge its cell shares with its parent, on the face it folds
  // onto, past the layers that come between once folded
  const create_foldable = (
    sheet: Cover,
    sheet_index: number,
    position: Vector3Type,
    rotation: EulerType
  ): FoldableMesh => {
    const is_y = get_is_y(settings)
    const { folds } = sheet
    const { height, width } = sheet.size
    const depth = sheet.size.depth * settings.sheet_fill
    const eps = Max(width, height) * 1e-9
    const near = (a: number, b: number) => Abs(a - b) <= eps
    const local = (x: number, y: number): [number, number] =>
      is_y ? [x - width / 2, y - height] : [x, y - height / 2]
    // Where a printed face reads the sheet at `x`, `y`: the back one mirrored
    const face_uv = (x: number, y: number, front: boolean): [number, number] => [
      front ? x / width : 1 - x / width,
      y / height,
    ]

    // How each cell turns: the way it lies off its parent, its hinge's
    // pivot and axis (see CreateFoldRig)
    const rig = CreateFoldRig(folds, depth, eps)
    const out = (i: number) => rig.out[i]!

    // The edge of a cell's box its parent's touches, the hinge it turns on:
    // a vertex on it goes with the parent
    const hinge = (i: number): ((x: number, y: number) => boolean) => {
      const fold = folds[i]!
      if (NullQ(fold.parent)) return Constant(false)
      const { box } = fold
      const [dx, dy] = out(i)
      if (dx) return x => near(x, dx > 0 ? box.x : box.x + box.width)
      return (_, y) => near(y, dy > 0 ? box.y : box.y + box.height)
    }

    // The die's outline, round the way a shape's contour is
    const outline = ShapeUtils.isClockWise(sheet.outline.map(({ x, y }) => new Vector2(x, y)))
      ? [...sheet.outline].reverse()
      : sheet.outline
    // What the die cuts out within it, each round the other way
    const holes = sheet.holes.map(points =>
      ShapeUtils.isClockWise(points.map(({ x, y }) => new Vector2(x, y)))
        ? points
        : [...points].reverse()
    )
    // Whether a point lies on the sheet, within the outline and no hole
    const on_sheet = (p: Point<number>) =>
      InsidePolygon(outline, p) && !holes.some(hole => InsidePolygon(hole, p))

    // `points` with no point repeated, the last not back on the first
    const ring = (points: Point<number>[]) =>
      points
        .filter((p, k) => !k || !near(p.x, points[k - 1]!.x) || !near(p.y, points[k - 1]!.y))
        .filter((p, k, all) => k < all.length - 1 || !near(p.x, all[0]!.x) || !near(p.y, all[0]!.y))

    // A slab of the sheet over `points`, a polygon round the way the outline
    // is, less the `inner` holes, that cell `i` draws: its faces printed, a
    // wall on each side the die cuts, none where it goes on into the sheet
    const die_slab = (
      points: Point<number>[],
      inner: Point<number>[][],
      i: number
    ): BufferGeometryType[] => {
      const contour = ring(points)
      if (contour.length < 3) return []
      const cut_out = inner.map(ring).filter(hole => hole.length >= 3)
      const triangles = ShapeUtils.triangulateShape(
        contour.map(({ x, y }) => new Vector2(x, y)),
        cut_out.map(hole => hole.map(({ x, y }) => new Vector2(x, y)))
      )
      if (!triangles.length) return []
      const on_hinge = hinge(i)
      const parent = folds[i]!.parent
      const positions: number[] = []
      const normals: number[] = []
      const uvs: number[] = []
      const skin: number[] = []
      const vertex = (x: number, y: number, z: number, normal: number[], uv: [number, number]) => {
        positions.push(...local(x, y), z)
        normals.push(...normal)
        uvs.push(...uv)
        skin.push(on_hinge(x, y) ? parent! : i, 0, 0, 0)
        return positions.length / 3 - 1
      }
      const rings = [contour, ...cut_out]
      const walls = rings.flatMap(edges =>
        edges.flatMap((a, k) => {
          const b = edges[(k + 1) % edges.length]!
          const [dx, dy] = [b.x - a.x, b.y - a.y]
          const length = Sqrt(dx ** 2 + dy ** 2)
          if (length <= eps) return []
          const normal = [dy / length, -dx / length, 0]
          const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
          const off = Max(width, height) * 1e-6
          if (on_sheet({ x: mid.x + normal[0]! * off, y: mid.y + normal[1]! * off })) return []
          const [at, ab, bb, bt] = [
            vertex(a.x, a.y, depth / 2, normal, [a.x / width, a.y / height]),
            vertex(a.x, a.y, -depth / 2, normal, [a.x / width, a.y / height]),
            vertex(b.x, b.y, -depth / 2, normal, [b.x / width, b.y / height]),
            vertex(b.x, b.y, depth / 2, normal, [b.x / width, b.y / height]),
          ]
          return [at, ab, bb, at, bb, bt]
        })
      )
      // The printed faces span the sheet, the back one mirrored; the
      // triangles number the contour's points, then each hole's
      const points_all = rings.flat()
      const front = points_all.map(({ x, y }) =>
        vertex(x, y, depth / 2, [0, 0, 1], face_uv(x, y, true))
      )
      const back = points_all.map(({ x, y }) =>
        vertex(x, y, -depth / 2, [0, 0, -1], face_uv(x, y, false))
      )
      const part = new BufferGeometry()
      part.setAttribute("position", new Float32BufferAttribute(positions, 3))
      part.setAttribute("normal", new Float32BufferAttribute(normals, 3))
      part.setAttribute("uv", new Float32BufferAttribute(uvs, 2))
      part.setAttribute("skinIndex", new Uint16BufferAttribute(skin, 4))
      part.setAttribute(
        "skinWeight",
        new Float32BufferAttribute([...Repeat([1, 0, 0, 0], positions.length / 3)].flat(), 4)
      )
      const faces = triangles.flatMap(([p, q, r]) => [front[p!]!, front[q!]!, front[r!]!])
      const backs = triangles.flatMap(([p, q, r]) => [back[r!]!, back[q!]!, back[p!]!])
      part.setIndex([...walls, ...faces, ...backs])
      part.addGroup(0, walls.length, 0)
      part.addGroup(walls.length, faces.length, FRONT)
      part.addGroup(walls.length + faces.length, backs.length, BACK)
      return [part]
    }

    // Each cell a box of its own, a line of vertices a sheet's thickness in
    // from each edge: the band from a hinge to its line is the crease that
    // bends. The box's grid is drawn within the cell's corners; on a sheet
    // its die cuts, each of the grid's boxes is the die's share of it
    const parts = folds.flatMap((fold, i): BufferGeometryType[] => {
      const { box, corners } = fold
      if (box.width <= eps || box.height <= eps) return []
      const lines = (from: number, size: number) =>
        [from, from + depth, from + size - depth, from + size]
          .filter(v => v >= from - eps && v <= from + size + eps)
          .sort((a, b) => a - b)
          .filter((v, k, all) => !k || !near(v, all[k - 1]!))
      const xs = lines(box.x, box.width)
      const ys = lines(box.y, box.height)
      if (outline.length)
        return xs.slice(1).flatMap((right, kx) =>
          ys.slice(1).flatMap((top, ky) => {
            const [left, bottom] = [xs[kx]!, ys[ky]!]
            const within = ({ x, y }: Point<number>) =>
              x > left + eps && x < right - eps && y > bottom + eps && y < top - eps
            return die_slab(
              ClipPolygon(outline, {
                x: left as Distance,
                y: bottom as Distance,
                width: (right - left) as Distance,
                height: (top - bottom) as Distance,
              }),
              // a hole the box holds whole
              holes.filter(hole => hole.every(within)),
              i
            )
          })
        )
      const part = new BoxGeometry(1, 1, depth, xs.length - 1, ys.length - 1, 1)
      const positions = part.attributes.position!
      const normals = part.attributes.normal!
      const uvs = part.attributes.uv!
      const on_hinge = hinge(i)
      const [bl, br, tr, tl] = corners
      const skin: number[] = []
      for (let v = 0; v < positions.count; v++) {
        const gx = xs[Round((positions.getX(v) + 0.5) * (xs.length - 1))]!
        const gy = ys[Round((positions.getY(v) + 0.5) * (ys.length - 1))]!
        const s = (gx - box.x) / box.width
        const t = (gy - box.y) / box.height
        const x = (1 - t) * ((1 - s) * bl!.x + s * br!.x) + t * ((1 - s) * tl!.x + s * tr!.x)
        const y = (1 - t) * ((1 - s) * bl!.y + s * br!.y) + t * ((1 - s) * tl!.y + s * tr!.y)
        positions.setXY(v, ...local(x, y))
        // The printed faces span the sheet, the back one mirrored
        const normal = normals.getZ(v)
        if (normal) uvs.setXY(v, ...face_uv(x, y, normal > 0))
        skin.push(on_hinge(gx, gy) ? fold.parent! : i, 0, 0, 0)
      }
      part.setAttribute("skinIndex", new Uint16BufferAttribute(skin, 4))
      part.setAttribute(
        "skinWeight",
        new Float32BufferAttribute([...Repeat([1, 0, 0, 0], positions.count)].flat(), 4)
      )
      merge_edge_groups(part)
      return [part]
    })
    const geometry = merge_parts(parts)
    geometry.computeBoundingSphere()
    geometry.computeBoundingBox()

    // Where each bone turns, laid flat, and on what axis (see CreateFoldRig)
    const pivots = rig.pivots.map(([x, y, z]) => new Vector3(...local(x, y), z))
    const axes = rig.axes.map(axis => new Vector3(...axis))

    const bones = folds.map(() => new Bone())
    folds.forEach((fold, i) => {
      const bone = bones[i]!
      if (NullQ(fold.parent)) bone.position.copy(pivots[i]!)
      else {
        bone.position.copy(pivots[i]!).sub(pivots[fold.parent]!)
        bones[fold.parent]!.add(bone)
      }
    })
    bones[0]!.visible = false
    const skeleton = new Skeleton(bones)

    const mesh = new SkinnedMesh(
      geometry,
      create_box_materials(sheet.paper_color, sheet.paper_roughness)
    )
    mesh.frustumCulled = false
    mesh.castShadow = true
    mesh.receiveShadow = true
    mesh.add(bones[0]!)
    mesh.bind(skeleton)
    mesh.position.copy(position)
    mesh.rotation.copy(rotation)

    return {
      angles: folds.map(fold => fold.angle),
      axes,
      bones,
      folds,
      mesh,
      paper_color: sheet.paper_color,
      paper_roughness: sheet.paper_roughness,
      position,
      print_back: sheet.print_rear,
      print_front: sheet.print_front,
      rotation,
      sheet_index,
      size: { depth: depth as Distance, height, width },
    }
  }

  // How far fold `i` is open, 0 folded to 1 flat
  const open_of = (foldable: FoldableMesh, i: number): number =>
    1 - foldable.angles[i]! / foldable.folds[i]!.angle

  // Whether a fold closes on top of the rest of its step: it stands ajar,
  // or hangs off one of its step that does
  const on_top = (folds: FoldCell[], i: number): boolean => {
    const fold = folds[i]!
    if (fold.ajar > 0) return true
    return (
      !NullQ(fold.parent) && folds[fold.parent]!.step === fold.step && on_top(folds, fold.parent)
    )
  }

  // Of the folds opening at `step`, those on top or under (any when `top`
  // is undefined), the least open or the most; none if none
  const step_open = (
    foldable: FoldableMesh,
    step: number,
    pick: (...values: number[]) => number,
    top: boolean | undefined
  ): number | undefined => {
    const opens = foldable.folds.flatMap((fold, i) =>
      i && fold.step === step && (NullQ(top) || on_top(foldable.folds, i) === top)
        ? [open_of(foldable, i)]
        : []
    )
    return opens.length ? pick(...opens) : undefined
  }

  // Each fold flat when `want` has it open, folded when not, a step at a
  // time: a fold opens once the step before is half open, and closes once
  // the step after is half closed; a step 0 fold never opens. Within a step
  // the folds on top open first and close last, once those under them are
  // half open or half closed: they never cut through them
  const fold_open = (foldable: FoldableMesh, want: (fold: FoldCell) => boolean): boolean[] =>
    foldable.folds.map((fold, i) => {
      if (!fold.step) return false
      const top = on_top(foldable.folds, i)
      if (want(fold))
        return (
          (fold.step === 1 || (step_open(foldable, fold.step - 1, Min, undefined) ?? 1) >= 0.5) &&
          (top || (step_open(foldable, fold.step, Min, true) ?? 1) >= 0.5)
        )
      return (
        (step_open(foldable, fold.step + 1, Max, undefined) ?? 0) > 0.5 ||
        (top && (step_open(foldable, fold.step, Max, false) ?? 0) > 0.5)
      )
    })

  // Every bone eased to its `targets` turn
  const update_foldable = (foldable: FoldableMesh, ease: number, targets: number[]) => {
    foldable.folds.forEach((fold, i) => {
      if (NullQ(fold.parent)) return
      foldable.angles[i]! += ease_delta(foldable.angles[i]!, targets[i]!, ease)
      foldable.bones[i]!.quaternion.setFromAxisAngle(foldable.axes[i]!, foldable.angles[i]!)
    })
  }

  // The sheet eased back to where it was laid, out of the stripe
  const place_foldable = (foldable: FoldableMesh, ease: number) => {
    const { mesh, position, rotation } = foldable
    for (const axis of ["x", "y", "z"] as const) {
      mesh.position[axis] += ease_delta(mesh.position[axis], position[axis], ease)
      mesh.rotation[axis] += ease_delta(mesh.rotation[axis], rotation[axis], ease)
    }
  }

  // How far a fold stands open or closed, at the viewer's ajar (see FoldStandOff)
  const open_target = (fold: FoldCell) => FoldOpenTarget(fold, settings.ajar)
  const closed_target = (fold: FoldCell, next: boolean) =>
    FoldClosedTarget(fold, next, settings.ajar)

  // The cover round the book: its boards open with the book, a flap on its
  // frame once the boards are halfway. An open flap stops its `ajar` short of
  // flat, and a closed one stands that ajar on its board while no page lies
  // on it: the front's before the first page turns, the back's once the last has
  const cover_targets = (cover: FoldableMesh): number[] => {
    const opened = is_book_opened()
    const side = (fold: FoldCell) => (is_front_flap(cover.folds, fold) ? "front" : "back")
    const open = fold_open(cover, fold =>
      fold.step === 1 ? opened || flaps_progress > 0.5 : flap_open() === side(fold)
    )
    const bare = opened && {
      back: page_frame() > get_total_sheets(settings),
      front: page_frame() <= 1,
    }
    return cover.folds.map((fold, i) => {
      if (fold.step < 2) return open[i] ? 0 : fold.angle
      if (open[i]) return open_target(fold)
      return closed_target(fold, !!bare && bare[side(fold)])
    })
  }

  // The corners of every cell of a foldable where its bones put them now
  const folding_corners = (foldable: FoldableMesh): Vector3Type[] => {
    foldable.mesh.updateMatrixWorld(true)
    const { boneInverses } = foldable.mesh.skeleton
    const { height, width } = foldable.size
    return foldable.folds.flatMap(({ corners }, i) => {
      const matrix = foldable.bones[i]!.matrixWorld.clone().multiply(boneInverses[i]!)
      return corners.map(({ x, y }) =>
        new Vector3(
          ...(get_is_y(settings) ? [x - width / 2, y - height] : [x, y - height / 2]),
          0
        ).applyMatrix4(matrix)
      )
    })
  }

  // A page folding: open up to the frame's step, then folding back, the last
  // to open the first to fold. As a cover's flaps, an open fold stops its
  // `ajar` short of flat, and the next to open stands that open
  const folding_targets = (foldable: FoldableMesh): number[] => {
    const step = Min(settings.frame_selected, frame_end() - settings.frame_selected)
    const open = fold_open(foldable, fold => fold.step <= step)
    return foldable.folds.map((fold, i) => {
      if (open[i]) return open_target(fold)
      return closed_target(fold, fold.step === step + 1)
    })
  }

  // Ideal view pointer (magenta arrow showing camera direction)
  const view_pointer = new ArrowHelper(
    new Vector3(0, 0, 1),
    new Vector3(0, 0, 0),
    1,
    0xff00ff,
    0.2,
    0.1
  )
  const vp_material = view_pointer.line.material as LineBasicMaterial
  vp_material.depthTest = false
  view_pointer.renderOrder = 999
  scene.add(view_pointer)

  // World origin marker
  const origin_marker = new AxesHelper(0.5) // X=red, Y=green, Z=blue
  const axes_material = origin_marker.material as LineBasicMaterial
  axes_material.linewidth = 5
  origin_marker.renderOrder = 999
  scene.add(origin_marker)
  scene.add(book_group)

  // Initialize pages based on initial settings
  rebuild_from_book()

  // Camera setup (after book is created)
  const { height, width } = get_book_dimensions(settings)
  const max_dimension = Max(height, width)
  const camera = new PerspectiveCamera(
    45,
    container.clientWidth / container.clientHeight,
    max_dimension * 0.01,
    max_dimension * 100
  )

  const resize = () => {
    camera.aspect = container.clientWidth / container.clientHeight
    camera.updateProjectionMatrix()
    renderer.setSize(container.clientWidth, container.clientHeight)
    invalidate()
  }

  const resize_observer = new ResizeObserver(resize)
  resize_observer.observe(container)

  // Calculate ideal camera distance based on settings.camera_zoom
  // camera_zoom: 0 = origin, 1 = perfect fit, >1 = more distant
  const get_ideal_camera_zoom = (): number => {
    if (settings.camera_zoom === 0) return 0

    let effective_width: number
    let effective_height: number

    const folding = !is_stripe_animation(settings) && get_folding(settings) && sheet_meshes[0]
    if (folding && "folds" in folding) {
      // A page folding fits what it covers now round the target, as the
      // camera sees it: closed a panel, open the sheet
      const target = get_ideal_camera_target()
      const up = get_ideal_camera_up()
      const right = new Vector3().crossVectors(up, get_ideal_camera_direction()).normalize()
      const offsets = folding_corners(folding).map(corner => corner.sub(target))
      effective_width = 2 * Max(...offsets.map(offset => Abs(offset.dot(right))))
      effective_height = 2 * Max(...offsets.map(offset => Abs(offset.dot(up))))
    } else if (is_stripe_animation(settings)) {
      const selected_item = get_selected_stripe_item()
      effective_width = selected_item.size.width
      effective_height = selected_item.size.height
    } else {
      const { height, width } = get_book_dimensions(settings)
      const is_y = get_is_y(settings)
      const mult = is_book_opened() ? 2 : 1
      effective_width = is_y ? width : width * mult
      effective_height = is_y ? height * mult : height
    }

    const half_fov = Tan(((camera.fov * PI) / 360) as Radian) // (camera.fov * (PI / 180)) / 2: degrees → radians, halved
    const distance_for_height = effective_height / 2 / half_fov
    const distance_for_width = effective_width / 2 / half_fov / camera.aspect

    return Max(distance_for_height, distance_for_width) * settings.camera_zoom
  }

  // Set camera to ideal position
  const ideal_direction = get_ideal_camera_direction()
  const ideal_target = get_ideal_camera_target()
  const cam_dist = get_ideal_camera_zoom()
  camera.position.copy(ideal_target).add(ideal_direction.multiplyScalar(cam_dist))

  const controls = new OrbitControls(camera, renderer.domElement)
  controls.target.copy(ideal_target)
  controls.enableDamping = true
  controls.update()

  // Lights (after camera)
  const ambient_light = new AmbientLight(0xffffff, 0)
  scene.add(ambient_light)

  // Unit offsets are (up, right, forward) in camera-relative coordinates (normalized)
  // Only north-west (in front of camera, on left) casts shadows
  const spot_light_offsets: { unit_offset: Vector3Type; shadow: boolean }[] = [
    { shadow: false, unit_offset: new Vector3(1, 1, 1) }, // north-east
    { shadow: true, unit_offset: new Vector3(1, -1, 1) }, // north-west
    { shadow: false, unit_offset: new Vector3(1, 1, -1) }, // south-east
    { shadow: true, unit_offset: new Vector3(1, -1, -1) }, // south-west
  ]
  const spot_lights: {
    light: SpotLightType
    marker: MeshType
    unit_offset: Vector3Type
  }[] = []
  for (const { unit_offset, shadow } of spot_light_offsets) {
    const light = new SpotLight(0xffffff, 0)
    light.angle = PI / 10
    light.penumbra = 0 // hard edge
    light.castShadow = shadow
    // Scale shadow parameters with scene dimensions
    light.shadow.mapSize.width = 2048
    light.shadow.mapSize.height = 2048
    light.shadow.radius = 1 // hard shadows (no blur)
    light.shadow.bias = -0.0001 // bias is in normalized depth space [0,1], no scaling needed
    // A sheet is thin and a box's walls take the light grazing: offset along
    // the surface's normal, at the scene's scale, so no wall shadows itself
    // in stripes (acne)
    light.shadow.normalBias = max_dimension * 0.002
    light.shadow.camera.near = max_dimension * 0.1
    light.shadow.camera.far = max_dimension * 10
    scene.add(light)
    scene.add(light.target)
    const marker_geometry = new BoxGeometry(1, 1, 0.5)
    merge_edge_groups(marker_geometry)
    const marker = new Mesh(
      marker_geometry,
      create_box_materials({ alpha: 1, blue: 0, green: 0, red: 0 } as RGBColor, 0.9)
    )
    marker.visible = false
    scene.add(marker)
    spot_lights.push({ light, marker, unit_offset })
  }

  const update_lighting = () => {
    ambient_light.intensity = settings.ambient_only ? 3 : 0.2
  }

  update_lighting()

  // Stop auto-return when user starts interacting, and wake the loop. "start"
  // fires on pointer-down and wheel, independent of the render loop, so it's a
  // reliable discrete wake source. (Not "change" — that fires from update() on
  // every sub-pixel damping/auto-return step, which would keep the loop awake
  // forever; the loop's own cam_moved threshold handles staying awake while the
  // camera visibly moves, then lets it settle.)
  controls.addEventListener("start", () => {
    is_animating_to_ideal = false
    invalidate()
    if (user_interaction_timeout) {
      clearTimeout(user_interaction_timeout)
      user_interaction_timeout = undefined
    }
  })

  // Resume auto-return after user stops interacting
  controls.addEventListener("end", () => {
    if (user_interaction_timeout) clearTimeout(user_interaction_timeout)
    user_interaction_timeout = setTimeout(() => {
      is_animating_to_ideal = true
      // May have gone idle during the delay — wake so auto-return can animate.
      invalidate()
    }, settings.camera_return_delay)
  })

  // Start animation loop
  animate()

  const get_page_size = (page_number: PageNumber) => {
    const [location] = page_to_sheet.get(page_number) ?? []
    if (!location) return undefined
    const sheet = sheet_meshes[location.sheet_index]
    return sheet.size
  }

  // Return API
  return {
    dispose,
    frame_end,
    frame_start,
    get_page_size,
    renderer: kind,
    resize,
    set_textures,
    update_model,
  }
}
